/**
 * Tab nào của trang CSKH hiện với ai.
 *
 * Trước 22/08/2026 trang dựng cả 5 tab cho bất cứ ai mở được URL — không sao,
 * vì chỉ người CSKH mới có menu vào. Từ khi lễ tân được mời vào để bán thêm thì
 * phải lọc, không thì lễ tân bước thẳng vào Khiếu nại và Quản lý CS.
 *
 * Rào cố ý KHÔNG áp cho người CSKH: ai đang có trang "Sửa Hình (CSKH)" giữ
 * nguyên đủ bộ thẻ như trước ngày này. Siết thêm cho nhóm đó là đổi thứ đang
 * chạy đúng — việc CEO chưa yêu cầu.
 *
 * 25/09/2026 bớt thẻ "Khiếu nại": trang Khiếu nại riêng (`/complaints`) đã làm
 * được mọi thứ thẻ này làm — hoàn tiền, chụp lại, sửa lại ảnh, ảnh bằng chứng
 * đều nằm trong hộp Kết luận ở bên đó — nên giữ hai đường vào chỉ tổ chia đôi
 * cùng một danh sách. Mọi vai đang mở được thẻ này đều đã mở được trang kia.
 */

import {
  BarChart3,
  CalendarDays,
  CircleDollarSign,
  IdCard,
  Images,
  Package,
  type LucideIcon,
} from "lucide-react";

import { hasAccess } from "@/lib/auth/page-access";
import type { Staff } from "@/lib/authv2/store";

// Biểu tượng là hình vector, KHÔNG phải emoji: emoji đổi hình theo hệ điều
// hành và trình duyệt, không chỉnh được cỡ/màu theo giao diện, và trình đọc
// màn hình đọc thành cả một câu ("biểu tượng khung ảnh") giữa tên tab.
export const CSKH_TABS = [
  // Booking online (CEO yêu cầu 30/09/2026): đổi tên từ "Booking" cho rõ đây là
  // lượt khách TỰ đặt trên trang đặt lịch ngoài, và đưa lên thẻ đầu — việc cần
  // xử sớm nhất trong ngày. Thẻ đầu cũng là thẻ mở sẵn khi đường dẫn không ghi
  // thẻ; lễ tân không thấy thẻ này nên vẫn rơi về "Đơn in & bán thêm".
  { key: "booking", label: "Booking online", icon: CalendarDays, pageId: "cskh_booking" },
  { key: "retouch", label: "Sửa hình", icon: Images, pageId: "cskh_retouch" },
  // Ảnh thẻ (CEO chốt câu 6, 16/09/2026): đơn ảnh thẻ tách khỏi danh sách Sửa
  // hình sang thẻ riêng. CHUNG pageId với "Sửa hình" vì đây là cùng một danh
  // sách chia đôi — chia thẻ không phải lý do để ai được thêm hay mất quyền.
  { key: "idphoto", label: "Ảnh thẻ", icon: IdCard, pageId: "cskh_retouch" },
  // Thẻ "Khiếu nại" và thẻ "Phản ánh ngoài" đều RỜI trang này 25/09/2026. Phốt
  // công khai chưa gắn được đơn nay ghi thẳng vào SỔ TIẾNG KHÁCH ở trang Khiếu
  // nại (QĐ 0207) — cùng một việc thì một chỗ, hai sổ song song là hai nguồn sự
  // thật rồi lệch nhau.
  { key: "upsale", label: "Đơn in & bán thêm", icon: Package, pageId: "cskh_upsale" },
  { key: "manage", label: "Quản lý", icon: BarChart3, pageId: "cskh_manage" },
  // Giá ảnh sửa: dùng CHUNG pageId với tab "Sửa hình" — ai vào được ô ảnh phát
  // sinh thì cũng cần TRA được bảng giá đang áp (nói giá cho khách). Quyền
  // SỬA giá là chuyện khác, gác riêng bằng `settings.write` bên trong tab.
  { key: "photo_price", label: "Giá ảnh sửa", icon: CircleDollarSign, pageId: "cskh_retouch" },
] as const satisfies readonly {
  key: string;
  label: string;
  icon: LucideIcon;
  pageId: string;
}[];

export type CskhTab = (typeof CSKH_TABS)[number];
export type CskhTabKey = CskhTab["key"];

export function visibleCskhTabs(staff: Staff | null | undefined): readonly CskhTab[] {
  if (!staff) return [];

  // Người của CSKH: giữ nguyên đủ bộ thẻ như trước 22/08/2026.
  if (hasAccess(staff, "cskh_retouch")) return CSKH_TABS;

  // Khách mời (lễ tân) chỉ thấy tab mà ma trận quyền trang thực sự cấp — TRỪ
  // Booking. `hasAccess` có nhánh riêng cho `cskh_booking` mở nó cho MỌI vai
  // "manager" bất kể phòng ban; luật ấy viết cho trưởng CSKH và trước nay chưa
  // lộ vì ngoài CSKH không ai vào được trang. Mời lễ tân vào bán thêm không
  // phải lý do để mở luôn lịch hẹn cho trưởng lễ tân.
  return CSKH_TABS.filter((t) => t.pageId !== "cskh_booking" && hasAccess(staff, t.pageId));
}
